import React, { useCallback, useId, useMemo, useRef, useState } from "react";
import {
  Activity,
  ArrowDownLeft,
  ArrowUpRight,
  CalendarDays,
  Clock3,
  Flame,
  Loader2,
  ShieldCheck,
  Trophy,
} from "lucide-react";
import {
  buildCategoryMix,
  buildDailyFlowSeries,
  buildHourHistogram,
  buildWalletInsights,
  compactMoney,
  dayKeyLabel,
  summarizeFlowSeries,
} from "../../utils/walletAnalytics";
import { txTypeLabel } from "../../utils/dashboardHelpers";

const RANGES = [
  { id: 7, label: "7D" },
  { id: 30, label: "30D" },
  { id: 90, label: "90D" },
  { id: "all", label: "All" },
];

const CHART_MODES = [
  { id: "cumulative", label: "Net flow" },
  { id: "daily", label: "Daily net" },
  { id: "flow", label: "In vs out" },
];

const VIEW_W = 640;
const VIEW_H = 210;
const PAD_TOP = 14;
const PAD_BOTTOM = 22;

const clamp01 = (v) => Math.min(1, Math.max(0, v));
const toneOf = (v) => (v > 0 ? "wa-up" : v < 0 ? "wa-down" : "");

/* ─────────────── Money flow chart (custom SVG, hover crosshair) ─────────────── */

const MoneyFlowChart = ({ series, mode, chartHeight = VIEW_H }) => {
  const gradId = useId();
  const surfaceRef = useRef(null);
  const [hoverIndex, setHoverIndex] = useState(null);

  const n = series.length;
  const hasActivity = series.some((p) => p.credits !== 0 || p.debits !== 0);

  const { yMin, yMax } = useMemo(() => {
    let lo = 0;
    let hi = 0;
    for (const p of series) {
      if (mode === "cumulative") {
        lo = Math.min(lo, p.cumulativeNet);
        hi = Math.max(hi, p.cumulativeNet);
      } else if (mode === "daily") {
        lo = Math.min(lo, p.net);
        hi = Math.max(hi, p.net);
      } else {
        lo = Math.min(lo, -p.debits);
        hi = Math.max(hi, p.credits);
      }
    }
    if (lo === 0 && hi === 0) hi = 1;
    const pad = (hi - lo) * 0.08;
    return { yMin: lo === 0 ? 0 : lo - pad, yMax: hi === 0 ? 0 : hi + pad };
  }, [series, mode]);

  const innerH = chartHeight - PAD_TOP - PAD_BOTTOM;
  const span = Math.max(yMax - yMin, 1e-9);
  const toY = useCallback(
    (v) => PAD_TOP + (1 - (v - yMin) / span) * innerH,
    [yMin, span, innerH]
  );
  const zeroY = toY(0);
  const slotW = n > 0 ? VIEW_W / n : VIEW_W;
  const centerX = useCallback((i) => (i + 0.5) * slotW, [slotW]);

  const { linePath, areaPath } = useMemo(() => {
    if (mode !== "cumulative" || !n) return { linePath: "", areaPath: "" };
    const pts =
      n === 1
        ? [
            { x: 0, y: toY(series[0].cumulativeNet) },
            { x: VIEW_W, y: toY(series[0].cumulativeNet) },
          ]
        : series.map((p, i) => ({ x: centerX(i), y: toY(p.cumulativeNet) }));
    let d = `M ${pts[0].x.toFixed(1)} ${pts[0].y.toFixed(1)}`;
    for (let i = 1; i < pts.length; i += 1) {
      d += ` L ${pts[i].x.toFixed(1)} ${pts[i].y.toFixed(1)}`;
    }
    const base = Math.min(Math.max(zeroY, PAD_TOP), chartHeight - PAD_BOTTOM);
    return {
      linePath: d,
      areaPath: `${d} L ${pts[pts.length - 1].x.toFixed(1)} ${base.toFixed(1)} L ${pts[0].x.toFixed(1)} ${base.toFixed(1)} Z`,
    };
  }, [mode, n, series, centerX, toY, zeroY, chartHeight]);

  const last = n ? series[n - 1].cumulativeNet : 0;
  const lineTone = last > 0 ? "up" : last < 0 ? "down" : "flat";

  const handleMove = (event) => {
    if (!n || !surfaceRef.current) return;
    const rect = surfaceRef.current.getBoundingClientRect();
    const frac = clamp01((event.clientX - rect.left) / rect.width);
    setHoverIndex(Math.min(n - 1, Math.floor(frac * n)));
  };

  const hover = hoverIndex != null ? series[hoverIndex] : null;
  const hoverXFrac = hoverIndex != null ? (hoverIndex + 0.5) / n : 0;

  const axisIndices = useMemo(() => {
    if (n <= 1) return n === 1 ? [0] : [];
    if (n <= 8) return series.map((_, i) => i);
    const out = [];
    for (let k = 0; k < 5; k += 1) out.push(Math.round((k / 4) * (n - 1)));
    return [...new Set(out)];
  }, [n, series]);

  if (!n || !hasActivity) {
    return (
      <div className="wa-chart-empty">
        <Activity size={15} />
        <span>No money movement in this window yet — the graph draws as you play.</span>
      </div>
    );
  }

  const barW = Math.min(slotW * 0.56, 30);
  const flowBarW = Math.min(slotW * 0.3, 14);

  return (
    <div
      ref={surfaceRef}
      className="wa-chart-surface"
      style={{ "--wa-chart-h": `${chartHeight}px` }}
      onPointerMove={handleMove}
      onPointerLeave={() => setHoverIndex(null)}
    >
      <svg
        viewBox={`0 0 ${VIEW_W} ${chartHeight}`}
        preserveAspectRatio="none"
        className="wa-chart-svg"
        role="img"
        aria-label="Wallet money flow chart"
      >
        <defs>
          <linearGradient id={`${gradId}-up`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#46c479" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#46c479" stopOpacity="0.01" />
          </linearGradient>
          <linearGradient id={`${gradId}-down`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ef5350" stopOpacity="0.01" />
            <stop offset="100%" stopColor="#ef5350" stopOpacity="0.26" />
          </linearGradient>
          <linearGradient id={`${gradId}-flat`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#64b5f6" stopOpacity="0.28" />
            <stop offset="100%" stopColor="#1e88e5" stopOpacity="0.01" />
          </linearGradient>
        </defs>

        {[yMax, (yMax + yMin) / 2, yMin, ...(yMin < 0 && yMax > 0 ? [0] : [])].map(
          (level, i) => (
            <line
              key={`${level}-${i}`}
              x1={0}
              x2={VIEW_W}
              y1={toY(level)}
              y2={toY(level)}
              className={`wa-grid${level === 0 ? " wa-grid-zero" : ""}`}
              vectorEffect="non-scaling-stroke"
            />
          )
        )}

        {mode === "cumulative" ? (
          <>
            <path d={areaPath} fill={`url(#${gradId}-${lineTone})`} />
            <path
              d={linePath}
              className={`wa-line wa-line-${lineTone}`}
              fill="none"
              vectorEffect="non-scaling-stroke"
            />
          </>
        ) : null}

        {mode === "daily"
          ? series.map((p, i) => {
              if (p.net === 0) return null;
              const y = toY(p.net);
              return (
                <rect
                  key={p.date}
                  x={centerX(i) - barW / 2}
                  y={Math.min(y, zeroY)}
                  width={barW}
                  height={Math.max(Math.abs(zeroY - y), 1.5)}
                  rx={2.5}
                  className={p.net > 0 ? "wa-bar-up" : "wa-bar-down"}
                  opacity={hoverIndex == null || hoverIndex === i ? 1 : 0.45}
                />
              );
            })
          : null}

        {mode === "flow"
          ? series.map((p, i) => {
              const cx = centerX(i);
              const dim = hoverIndex != null && hoverIndex !== i;
              return (
                <g key={p.date} opacity={dim ? 0.45 : 1}>
                  {p.credits > 0 ? (
                    <rect
                      x={cx - flowBarW - 1}
                      y={toY(p.credits)}
                      width={flowBarW}
                      height={Math.max(zeroY - toY(p.credits), 1.5)}
                      rx={2}
                      className="wa-bar-up"
                    />
                  ) : null}
                  {p.debits > 0 ? (
                    <rect
                      x={cx + 1}
                      y={zeroY}
                      width={flowBarW}
                      height={Math.max(toY(-p.debits) - zeroY, 1.5)}
                      rx={2}
                      className="wa-bar-down"
                    />
                  ) : null}
                </g>
              );
            })
          : null}

        {hover ? (
          <line
            x1={hoverXFrac * VIEW_W}
            x2={hoverXFrac * VIEW_W}
            y1={PAD_TOP}
            y2={chartHeight - PAD_BOTTOM}
            className="wa-crosshair"
            vectorEffect="non-scaling-stroke"
          />
        ) : null}
      </svg>

      <div className="wa-yaxis" aria-hidden>
        <span>{compactMoney(yMax)}</span>
        {yMin < 0 && yMax > 0 ? <span>0</span> : null}
        <span>{compactMoney(yMin)}</span>
      </div>

      <div className="wa-xaxis" aria-hidden>
        {axisIndices.map((i) => (
          <span key={series[i]?.date || i} style={{ left: `${((i + 0.5) / n) * 100}%` }}>
            {dayKeyLabel(series[i]?.date)}
          </span>
        ))}
      </div>

      {hover ? (
        <div
          className={`wa-tooltip tabular-nums${hoverXFrac > 0.7 ? " wa-tooltip-left" : ""}`}
          style={{ left: `${hoverXFrac * 100}%` }}
        >
          <em>{dayKeyLabel(hover.date)}</em>
          {mode === "flow" ? (
            <>
              <span className="wa-up">In {compactMoney(hover.credits)}</span>
              <span className="wa-down">Out {compactMoney(hover.debits)}</span>
              <strong className={toneOf(hover.net)}>Net {compactMoney(hover.net)}</strong>
            </>
          ) : (
            <>
              <strong className={toneOf(mode === "cumulative" ? hover.cumulativeNet : hover.net)}>
                {compactMoney(mode === "cumulative" ? hover.cumulativeNet : hover.net)}
              </strong>
              <span>{hover.count} transaction{hover.count === 1 ? "" : "s"}</span>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
};

/* ─────────────── Activity mix (where the money goes) ─────────────── */

const ActivityMix = ({ mix }) => {
  const peak = Math.max(...mix.map((r) => r.volume), 1);

  return (
    <section className="wa-card">
      <header className="wa-card-head">
        <div>
          <span className="wa-kicker">Activity mix</span>
          <p className="wa-caption">Where your money moves, by volume</p>
        </div>
      </header>

      {!mix.length ? (
        <p className="wa-empty-copy">Your mix appears once transactions land in this window.</p>
      ) : (
        <ul className="wa-mix-list">
          {mix.slice(0, 7).map((row) => {
            const width = Math.max((row.volume / peak) * 100, 4);
            return (
              <li key={row.family} className="wa-mix-row">
                <span className="wa-mix-swatch" style={{ background: row.color }} aria-hidden />
                <div className="wa-mix-copy">
                  <div className="wa-mix-top">
                    <strong>{row.label}</strong>
                    <em className={`tabular-nums ${toneOf(row.net)}`}>
                      {row.net > 0 ? "+" : ""}
                      {compactMoney(row.net)}
                    </em>
                  </div>
                  <span className="wa-mix-track" aria-hidden>
                    <span
                      className="wa-mix-fill"
                      style={{ width: `${width}%`, background: row.color }}
                    />
                  </span>
                  <span className="wa-mix-hint">
                    {row.count} tx · in {compactMoney(row.inflow)} · out {compactMoney(row.outflow)}
                  </span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

/* ─────────────── Rhythm (when you play) ─────────────── */

const HOUR_MARKS = [0, 6, 12, 18, 23];

const WalletRhythm = ({ transactions, insights }) => {
  const { buckets, max, peakHour } = useMemo(
    () => buildHourHistogram(transactions),
    [transactions]
  );
  const total = transactions.length;

  return (
    <section className="wa-card">
      <header className="wa-card-head">
        <div>
          <span className="wa-kicker">Rhythm</span>
          <p className="wa-caption">When your wallet is most active</p>
        </div>
        {peakHour != null ? (
          <span className="wa-rhythm-peak">
            <Clock3 size={11} />
            Peak {String(peakHour).padStart(2, "0")}:00
          </span>
        ) : null}
      </header>

      {!total ? (
        <p className="wa-empty-copy">Hourly rhythm appears once you have activity here.</p>
      ) : (
        <>
          <div className="wa-hours" role="img" aria-label="Transactions by hour of day">
            {buckets.map((count, hour) => (
              <span
                key={hour}
                className={`wa-hour${hour === peakHour ? " wa-hour-peak" : ""}`}
                style={{ opacity: count ? 0.25 + (count / max) * 0.75 : 0.08 }}
                title={`${String(hour).padStart(2, "0")}:00 · ${count} tx`}
              />
            ))}
          </div>
          <div className="wa-hours-axis" aria-hidden>
            {HOUR_MARKS.map((h) => (
              <span key={h} style={{ left: `${((h + 0.5) / 24) * 100}%` }}>
                {String(h).padStart(2, "0")}
              </span>
            ))}
          </div>

          <dl className="wa-rhythm-facts">
            <div>
              <dt>Busiest day</dt>
              <dd className="tabular-nums">
                {insights.busiestDay
                  ? `${dayKeyLabel(insights.busiestDay.date)} · ${insights.busiestDay.count} tx`
                  : "—"}
              </dd>
            </div>
            <div>
              <dt>Pending now</dt>
              <dd className="tabular-nums">{insights.pendingCount}</dd>
            </div>
            <div>
              <dt>Failed / cancelled</dt>
              <dd className="tabular-nums">{insights.failedCount}</dd>
            </div>
          </dl>
        </>
      )}
    </section>
  );
};

/* ─────────────── Main board ─────────────── */

const WalletAnalyticsBoard = ({
  transactions = [],
  loading = false,
  chartHeight = VIEW_H,
  className = "",
}) => {
  const [range, setRange] = useState(30);
  const [chartMode, setChartMode] = useState("cumulative");

  const windowTx = useMemo(() => {
    if (range === "all") return transactions;
    const cutoff = new Date();
    cutoff.setHours(0, 0, 0, 0);
    cutoff.setDate(cutoff.getDate() - (Number(range) - 1));
    return transactions.filter((tx) => tx?.createdAt && new Date(tx.createdAt) >= cutoff);
  }, [transactions, range]);

  const series = useMemo(() => buildDailyFlowSeries(transactions, range), [transactions, range]);
  const flow = useMemo(() => summarizeFlowSeries(series), [series]);
  const mix = useMemo(() => buildCategoryMix(windowTx), [windowTx]);
  const insights = useMemo(() => buildWalletInsights(windowTx), [windowTx]);

  const moved = flow.credits + flow.debits;
  const netTone = toneOf(flow.net);

  if (loading && !transactions.length) {
    return (
      <div className="wa-card wa-chart-empty">
        <Loader2 size={16} className="animate-spin" />
        <span>Crunching your wallet data…</span>
      </div>
    );
  }

  return (
    <div className={`wa-board${className ? ` ${className}` : ""}`} aria-label="Wallet analytics">
      {/* ── Hero: net flow for the window ── */}
      <section className="wa-card wa-hero">
        <div className="wa-hero-main">
          <span className="wa-kicker">
            Net flow · {RANGES.find((r) => r.id === range)?.label || range}
          </span>
          <strong className={`wa-hero-value tabular-nums ${netTone}`}>
            {flow.net > 0 ? "+" : flow.net < 0 ? "−" : ""}
            {compactMoney(Math.abs(flow.net))}
          </strong>
          <span className="wa-hero-sub">
            {moved > 0
              ? `${compactMoney(moved)} moved across ${flow.count} transactions (cash-equivalent)`
              : "No money movement in this window yet"}
          </span>
        </div>

        <div className="wa-hero-tiles">
          <div className="wa-tile">
            <span>Money in</span>
            <strong className="tabular-nums wa-up">{compactMoney(flow.credits)}</strong>
          </div>
          <div className="wa-tile">
            <span>Money out</span>
            <strong className="tabular-nums wa-down">{compactMoney(flow.debits)}</strong>
          </div>
          <div className="wa-tile">
            <span>Transactions</span>
            <strong className="tabular-nums">{flow.count.toLocaleString()}</strong>
          </div>
          <div className="wa-tile">
            <span>Success rate</span>
            <strong className="tabular-nums">
              {insights.successRate != null ? `${insights.successRate}%` : "—"}
            </strong>
          </div>
        </div>

        <div className="wa-ranges" role="tablist" aria-label="Analytics range">
          {RANGES.map((r) => (
            <button
              key={r.id}
              type="button"
              role="tab"
              aria-selected={range === r.id}
              className={`wa-range${range === r.id ? " wa-range-on" : ""}`}
              onClick={() => setRange(r.id)}
            >
              {r.label}
            </button>
          ))}
        </div>
      </section>

      {/* ── Money flow chart ── */}
      <section className="wa-card">
        <header className="wa-card-head">
          <div>
            <span className="wa-kicker">Money flow</span>
            <p className="wa-caption">
              {chartMode === "flow"
                ? "Money in vs money out, day by day"
                : chartMode === "daily"
                ? "Daily net result of your wallet"
                : "Running net flow across the window"}
            </p>
          </div>
          <div className="wa-toggle-row">
            {CHART_MODES.map((m) => (
              <button
                key={m.id}
                type="button"
                className={`wa-toggle${chartMode === m.id ? " wa-toggle-on" : ""}`}
                onClick={() => setChartMode(m.id)}
              >
                {m.label}
              </button>
            ))}
          </div>
        </header>

        <MoneyFlowChart series={series} mode={chartMode} chartHeight={chartHeight} />
      </section>

      {/* ── Insight tiles ── */}
      <div className="wa-insights">
        <article className="wa-insight">
          <span className="wa-insight-icon wa-up">
            <ArrowDownLeft size={12} />
          </span>
          <div>
            <span className="wa-insight-label">Biggest inflow</span>
            <strong className={`tabular-nums ${insights.biggestIn ? "wa-up" : ""}`}>
              {insights.biggestIn ? `+${compactMoney(insights.biggestIn.flow)}` : "—"}
            </strong>
            <em>
              {insights.biggestIn
                ? txTypeLabel(insights.biggestIn.tx.transactionType)
                : "No inflow yet"}
            </em>
          </div>
        </article>
        <article className="wa-insight">
          <span className="wa-insight-icon wa-down">
            <ArrowUpRight size={12} />
          </span>
          <div>
            <span className="wa-insight-label">Biggest outflow</span>
            <strong className={`tabular-nums ${insights.biggestOut ? "wa-down" : ""}`}>
              {insights.biggestOut ? compactMoney(insights.biggestOut.flow) : "—"}
            </strong>
            <em>
              {insights.biggestOut
                ? txTypeLabel(insights.biggestOut.tx.transactionType)
                : "No outflow yet"}
            </em>
          </div>
        </article>
        <article className="wa-insight">
          <span className="wa-insight-icon">
            <Trophy size={12} />
          </span>
          <div>
            <span className="wa-insight-label">Wins landed</span>
            <strong className={`tabular-nums ${insights.winCount ? "wa-up" : ""}`}>
              {insights.winCount ? `+${compactMoney(insights.winTotal)}` : "—"}
            </strong>
            <em>
              {insights.winCount
                ? `across ${insights.winCount} winning payouts`
                : "No wins in this window"}
            </em>
          </div>
        </article>
        <article className="wa-insight">
          <span className="wa-insight-icon wa-up">
            <Flame size={12} />
          </span>
          <div>
            <span className="wa-insight-label">Best day</span>
            <strong className={`tabular-nums ${flow.best ? "wa-up" : ""}`}>
              {flow.best ? `+${compactMoney(flow.best.net)}` : "—"}
            </strong>
            <em>{flow.best ? dayKeyLabel(flow.best.date) : "No green day yet"}</em>
          </div>
        </article>
        <article className="wa-insight">
          <span className="wa-insight-icon wa-down">
            <ShieldCheck size={12} />
          </span>
          <div>
            <span className="wa-insight-label">Toughest day</span>
            <strong className={`tabular-nums ${flow.worst ? "wa-down" : ""}`}>
              {flow.worst ? compactMoney(flow.worst.net) : "—"}
            </strong>
            <em>{flow.worst ? dayKeyLabel(flow.worst.date) : "No losing day"}</em>
          </div>
        </article>
        <article className="wa-insight">
          <span className="wa-insight-icon">
            <CalendarDays size={12} />
          </span>
          <div>
            <span className="wa-insight-label">Daily average</span>
            <strong className="tabular-nums">
              {series.length ? compactMoney(moved / series.length) : "—"}
            </strong>
            <em>moved per day in window</em>
          </div>
        </article>
      </div>

      {/* ── Mix + rhythm ── */}
      <div className="wa-duo">
        <ActivityMix mix={mix} />
        <WalletRhythm transactions={windowTx} insights={insights} />
      </div>
    </div>
  );
};

export default WalletAnalyticsBoard;
